import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/landing/Footer';
import { AnimatedCounter } from '../components/landing/AnimatedCounter';
import { CombatTrackerMockup } from '../components/landing/mockups/CombatTrackerMockup';
import { MapMockup } from '../components/landing/mockups/MapMockup';
import { CharacterSheetMockup } from '../components/landing/mockups/CharacterSheetMockup';
import { LoreWikiMockup } from '../components/landing/mockups/LoreWikiMockup';
import { toast } from 'sonner';
import {
  ChevronRight,
  Swords,
  Map,
  BookOpen,
  FileText,
  Shield,
  Users,
  Compass,
  ArrowRight,
  Mail,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

/* ── Beta signup form (closed beta waitlist) ── */
function BetaSignupForm({ language }) {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !consent) return;
    setLoading(true);
    try {
      await axios.post('/api/beta/signup', {
        email,
        gdpr_consent: consent,
        website,
      });
      setSubmitted(true);
      toast.success(
        language === 'fr'
          ? 'Demande enregistrée ! Vérifiez vos emails.'
          : 'Request submitted! Check your email.'
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' && detail) ||
          (language === 'fr' ? 'Une erreur est survenue' : 'Something went wrong')
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
          <h3 className="font-cinzel text-xl font-bold mb-2">
            {language === 'fr' ? 'Demande enregistrée !' : 'Request received!'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {language === 'fr'
              ? "Nous vous avons envoyé un email de confirmation. Vous recevrez une invitation personnelle dès qu'une place se libère."
              : "We've sent you a confirmation email. You'll receive a personal invite as soon as a slot opens up."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardContent className="p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-3">
            <Mail className="h-6 w-6" />
          </div>
          <h3 className="font-cinzel text-xl sm:text-2xl font-bold mb-2">
            {language === 'fr' ? 'Rejoignez la beta fermée' : 'Join the closed beta'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {language === 'fr'
              ? "L'accès est actuellement sur invitation. Laissez votre email pour être sur la liste d'attente."
              : 'Access is currently invite-only. Leave your email to join the waitlist.'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="beta-signup-form">
          {/* honeypot — invisible for humans, visible for basic bots */}
          <input
            type="text"
            name="website"
            tabIndex="-1"
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: 'absolute', left: '-5000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
            aria-hidden="true"
          />
          <Input
            type="email"
            placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="beta-signup-email"
          />
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
              className="mt-0.5"
              data-testid="beta-signup-consent"
            />
            <span>
              {language === 'fr'
                ? "J'accepte que mon adresse email soit stockée pour gérer ma demande d'accès à la beta. "
                : 'I agree to have my email stored to manage my beta access request. '}
              <Link to="/mentions-legales" className="text-primary hover:underline">
                {language === 'fr' ? 'Politique de confidentialité' : 'Privacy policy'}
              </Link>
            </span>
          </label>
          <Button
            type="submit"
            className="w-full fantasy-button"
            disabled={loading || !email || !consent}
            data-testid="beta-signup-submit"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('loading', language)}</>
            ) : (
              language === 'fr' ? "Demander l'accès" : 'Request access'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/* ── Section reveal on scroll ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('landing-revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function RevealSection({ children, className = '' }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`landing-section-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const { isAuthenticated, language } = useAuth();
  const navigate = useNavigate();
  const [betaMode, setBetaMode] = useState(false);

  useEffect(() => {
    axios
      .get('/api/beta/status')
      .then((res) => setBetaMode(!!res.data?.beta_mode_enabled))
      .catch(() => setBetaMode(false));
  }, []);

  const heroFeatures = [
    {
      icon: Swords,
      title: t('landing_feature_5_title', language),
      description: t('landing_feature_5_desc', language),
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      mockup: <CombatTrackerMockup />,
    },
    {
      icon: Map,
      title: t('landing_feature_3_title', language),
      description: t('landing_feature_7_desc', language),
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      mockup: <MapMockup />,
    },
    {
      icon: BookOpen,
      title: t('landing_feature_8_title', language),
      description: t('landing_feature_8_desc', language),
      color: 'text-violet-400',
      bg: 'bg-violet-500/10',
      mockup: <LoreWikiMockup />,
    },
    {
      icon: FileText,
      title: t('landing_feature_6_title', language),
      description: t('landing_feature_6_desc', language),
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      mockup: <CharacterSheetMockup />,
    },
  ];

  const useCases = [
    {
      icon: Shield,
      title: t('landing_use_case_gm_title', language),
      description: t('landing_use_case_gm_desc', language),
      color: 'text-red-400',
      border: 'border-red-500/20 hover:border-red-500/40',
    },
    {
      icon: Users,
      title: t('landing_use_case_player_title', language),
      description: t('landing_use_case_player_desc', language),
      color: 'text-blue-400',
      border: 'border-blue-500/20 hover:border-blue-500/40',
    },
    {
      icon: Compass,
      title: t('landing_use_case_worldbuilder_title', language),
      description: t('landing_use_case_worldbuilder_desc', language),
      color: 'text-purple-400',
      border: 'border-purple-500/20 hover:border-purple-500/40',
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/10 dark:via-background" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-primary/5 blur-3xl dark:bg-primary/8" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
          <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6">
            <span className="text-foreground">{language === 'fr' ? 'Votre Campagne.' : 'Your Campaign.'}</span>
            <br />
            <span className="text-primary">{language === 'fr' ? 'Votre Légende.' : 'Your Legend.'}</span>
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('landing_hero_subtitle_v2', language)}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="fantasy-button text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 group"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/dashboard');
                } else if (betaMode) {
                  const el = document.getElementById('beta-signup');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                  navigate('/register');
                }
              }}
              data-testid="cta-button"
            >
              {!isAuthenticated && betaMode
                ? (language === 'fr' ? "Demander l'accès beta" : 'Request beta access')
                : t('landing_cta', language)}
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            {!isAuthenticated && (
              <Button
                variant="outline"
                size="lg"
                className="px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg"
                onClick={() => navigate('/login')}
                data-testid="login-cta"
              >
                {t('login', language)}
              </Button>
            )}
          </div>

          {!isAuthenticated && betaMode && (
            <p className="mt-6 text-xs uppercase tracking-widest text-primary/80 font-medium">
              {language === 'fr' ? '🔒 Beta fermée — sur invitation' : '🔒 Closed beta — invite-only'}
            </p>
          )}
        </div>
      </section>

      {/* ═══════════ BETA SIGNUP ═══════════ */}
      {!isAuthenticated && betaMode && (
        <RevealSection>
          <section id="beta-signup" className="py-16 sm:py-20 px-4">
            <div className="max-w-xl mx-auto">
              <BetaSignupForm language={language} />
            </div>
          </section>
        </RevealSection>
      )}

      {/* ═══════════ STATS BAR ═══════════ */}
      <RevealSection>
        <section className="py-12 sm:py-16 px-4 bg-secondary/20 border-y border-border/50">
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-sm text-muted-foreground mb-8 font-medium uppercase tracking-wider">
              {t('landing_trusted_by', language)}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 text-center">
              {[
                { value: 1200, suffix: '+', label: t('landing_section_stats_campaigns', language) },
                { value: 8500, suffix: '+', label: t('landing_section_stats_sessions', language) },
                { value: 25000, suffix: '+', label: t('landing_section_stats_npcs', language) },
                { value: 3400, suffix: '+', label: t('landing_section_stats_maps', language) },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-black text-primary">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* ═══════════ 4 KEY FEATURES ═══════════ */}
      <RevealSection>
        <section className="py-20 sm:py-28 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                {t('landing_section_visual_title', language)}
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                {t('landing_section_visual_subtitle', language)}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {heroFeatures.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={i}
                    className="group border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg overflow-hidden"
                  >
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2.5 rounded-xl ${feature.bg} ${feature.color} group-hover:scale-110 transition-transform`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-cinzel font-bold text-base">{feature.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                        </div>
                      </div>
                      {feature.mockup}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group"
              >
                {t('landing_features_see_all', language)}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* ═══════════ USE CASES ═══════════ */}
      <RevealSection>
        <section className="py-20 sm:py-28 px-4 bg-secondary/10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Conçu pour Chaque Rôle' : 'Built for Every Role'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {useCases.map((uc, i) => {
                const Icon = uc.icon;
                return (
                  <Card key={i} className={`border ${uc.border} transition-all duration-300`}>
                    <CardContent className="p-6 text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-card border border-border/50 mb-4 ${uc.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-cinzel font-bold text-lg mb-2">{uc.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{uc.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <RevealSection>
        <section className="py-20 sm:py-28 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <h2 className="font-cinzel text-2xl sm:text-4xl lg:text-5xl font-bold mb-5">
              {language === 'fr' ? 'Prêt à Forger Votre Légende ?' : 'Ready to Forge Your Legend?'}
            </h2>
            <p className="text-muted-foreground mb-10 max-w-xl mx-auto text-base sm:text-lg">
              {language === 'fr'
                ? 'Rejoignez des centaines de tables qui vivent des aventures inoubliables.'
                : 'Join hundreds of tables experiencing unforgettable adventures.'}
            </p>
            <Button
              size="lg"
              className="fantasy-button text-base sm:text-lg px-10 sm:px-12 py-6 sm:py-7 group"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/dashboard');
                } else if (betaMode) {
                  const el = document.getElementById('beta-signup');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                  navigate('/register');
                }
              }}
              data-testid="footer-cta"
            >
              {!isAuthenticated && betaMode
                ? (language === 'fr' ? "Demander l'accès beta" : 'Request beta access')
                : t('landing_cta', language)}
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>
      </RevealSection>

      <Footer />
    </div>
  );
}
